import { Box, Flex } from '@withjoy/joykit';
import React, { useEffect, useState } from 'react';
import { TileGrid } from '@apps/card/components/TileGrid';
import { RAIL_OPEN_GALLERY_COLUMNS, gridTemplateColumnsFor } from '@apps/card/components/TileGrid/galleryColumns';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { useCardCategoryContext } from '@apps/card/components/CardCategoryProvider';
import { PageSection } from '@apps/card/components/PageSection';
import { useCardTelemetry, TelemetryProvider, enrichCardTelemetryExtraInfo } from '@apps/card/Card.telemetry';
import { useDesignTemplates } from './useDesignTemplates';
import { useDesignGalleryItems } from './useDesignGalleryItems';
import type { CustomizeNowClickOverrideHandler, StationeryTemplate } from '@apps/card/types/stationeryTemplate.types';
import { GalleryItem } from './components/GalleryItem/GalleryItem';
import { useConciergeDisclosure } from './useConciergeDisclosure';
import { BackToTop } from '@shared/components/BackToTop';
import { DesignGalleryFiltersProvider } from './DesignGalleryFiltersProvider';
import { SearchAndFiltersContainer } from './components/Filters/SearchAndFilterContainer';
import { FILTERS_SIDEBAR_ID, FILTERS_SIDEBAR_WIDTH, FiltersSidebar } from './components/Filters/components/FiltersSidebar';
import { useQueryParamHelper } from '@shared/core/queryString';
import { GallerySeo } from './DesignsGallerySeo';
import { CardFormat } from '../ProductDetailsPage/constants';
import { useFreeShippingBanner } from '@apps/card/hooks/useFreeShippingBanner';
import { ItemImpressionsTelemetryProvider } from './components/ItemImpressionsTelemetry';
import { FaqSection } from '@apps/card/routes/PublicShop/components/FaqSection';
import { useStationeryFaqItems } from '@apps/card/routes/PublicShop/components/FaqSection/useStationeryFaqItems';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import { SeoContentSection } from '@apps/card/routes/PublicShop/components/SeoContentSection';
import { RelatedLinks } from '@apps/card/routes/PublicShop/components/RelatedLinks';
import { SeoReviewsSection } from '@apps/card/routes/PublicShop/components/SeoReviewsSection';
import { useFilterFacets } from './useFilterFacets';
import { useTemplateFacetIndex } from './useTemplateFacetIndex';
import type { TemplateFacetIndex } from './templateFacetMatching.types';
import { useProductsWithStatus } from '@apps/card/hooks/useProducts';
import { DesignsGallerySkeleton } from './DesignsGallerySkeleton';
import { NoResultsFound } from './NoResultsFound';
import { MatchTierDivider } from './components/MatchTierDivider';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { GalleryColorwayOrderProvider } from './GalleryColorwayOrderContext';
import { GallerySearchScoresProvider } from './GallerySearchScoresContext';
import type { ColorHits } from './useColorHits';
import { useColorHits } from './useColorHits';
import { StyleShowcase } from './components/StyleShowcase';
import { GalleryPhotoPreview } from './GalleryPhotoPreview';
import { SaveTheDateMarcomFooter } from './SaveTheDateMarcomFooter.lazy';
import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';

const RAIL_OPEN_GALLERY_GRID_COLUMNS = gridTemplateColumnsFor(RAIL_OPEN_GALLERY_COLUMNS);

/** Left gutter of the filter rail and the indent of the chips row when the rail is shut (32px). */
const RAIL_GUTTER_PX = 32;

type DesignsGalleryProps = Readonly<{
  eventId?: string;
  eventHandle?: string;
  // Intended only to be used for the investor demo.
  onCustomizeCardClickOverride?: CustomizeNowClickOverrideHandler;
}>;

const Root = ({
  eventId, // Only used for FavoriteBadge
  eventHandle,
  onCustomizeCardClickOverride,
  templates,
  colorHits,
  facetIndex,
}: {
  templates: ReadonlyArray<StationeryTemplate>;
  colorHits: ColorHits;
  facetIndex: TemplateFacetIndex;
} & DesignsGalleryProps) => {
  useCardTelemetry({ page: 'homeRoute' });
  // Inside DesignGalleryFiltersProvider: the facet counts are scoped to the active selection.
  const filterFacets = useFilterFacets(templates, facetIndex);
  const { currentCategory } = useCardCategoryContext();
  enrichCardTelemetryExtraInfo({ stationeryTemplateCategory: currentCategory });
  useEffect(() => {
    enrichCardTelemetryExtraInfo({ stationeryTemplateCategory: null });
  }, []);

  const { ConciergeDialog, onCardSelect } = useConciergeDisclosure(onCustomizeCardClickOverride);

  const params = useQueryParamHelper();
  const preferredFormat = (params.getValue('format') as CardFormat | undefined) ?? CardFormat.paper;
  // The pill that was clicked owns the rail; clicking it again closes the rail.
  const [facetSidebarGroup, setFacetSidebarGroup] = useState<string | null>(null);
  const toggleFacetSidebar = (groupCode: string) => setFacetSidebarGroup(current => (current === groupCode ? null : groupCode));
  const facetSidebarFacets = filterFacets.filter(facet => facet.groupCode === facetSidebarGroup);
  // Mobile swaps the pills for a single drawer trigger, so it never shows the rail.
  const isMobileScreen = useIsMobileScreen();
  const isRailOpen = !isMobileScreen && facetSidebarFacets.length > 0;
  // One column fewer while the rail is open, so tiles keep the width their content was drawn for.
  const { items, hasNoSearchResults, colorwayOrder, searchScoresByItem, partialMatchIds } = useDesignGalleryItems({ templates, colorHits, facetIndex, isRailOpen });
  // The exact matches come first; the divider sits before the first design that matches only
  // some of the selected filters, on whichever page that boundary falls.
  const partialMatchesStartAt = partialMatchIds.size ? items.findIndex(data => data.type === 'template' && partialMatchIds.has(data.id)) : -1;
  const hasNoExactMatches = partialMatchesStartAt === 0;

  return (
    <GalleryPhotoPreview
      eventId={eventId}
      eventHandle={eventHandle}
    >
      {previewAction => (
        <>
          {ConciergeDialog}
          <PageSection
            id="designs-gallery"
            paddingX={0}
            spacing={6}
            marginTop={9}
            titleMarginX={{ _: 6, md: 8 }}
          >
            <SearchAndFiltersContainer
              filterFacets={filterFacets}
              trailingAction={previewAction}
              facetSidebar={{ activeGroupCode: isRailOpen ? facetSidebarGroup : null, onToggle: toggleFacetSidebar, railId: FILTERS_SIDEBAR_ID }}
            />
            {(() => {
              if (hasNoSearchResults) return <NoResultsFound />;

              const grid = (
                <GalleryColorwayOrderProvider value={colorwayOrder}>
                  <GallerySearchScoresProvider value={searchScoresByItem}>
                    <TileGrid columns={isRailOpen ? RAIL_OPEN_GALLERY_GRID_COLUMNS : undefined}>
                      {items.map((data, index) => (
                        <React.Fragment key={data.type === 'template' ? (data.instanceKey ?? data.id) : index}>
                          {index === partialMatchesStartAt && <MatchTierDivider noExactMatches={hasNoExactMatches} />}
                          <GalleryItem
                            preferredFormat={preferredFormat}
                            cardTileSource="shop"
                            eventId={eventId}
                            onCardSelect={onCardSelect}
                            data={data}
                            currentCategory={currentCategory}
                            index={index}
                          />
                        </React.Fragment>
                      ))}
                    </TileGrid>
                  </GallerySearchScoresProvider>
                </GalleryColorwayOrderProvider>
              );

              return (
                // No gutter on the row itself: the grid stays flush with the page edge. The rail
                // carries its own left gutter, which collapses with it.
                <Flex alignItems="flex-start">
                  {/* Collapses to nothing when hidden; the rail keeps its width inside so the
                        content doesn't reflow while the wrapper animates. */}
                  <Box
                    id={FILTERS_SIDEBAR_ID}
                    flex="none"
                    width={isRailOpen ? FILTERS_SIDEBAR_WIDTH + RAIL_GUTTER_PX : 0}
                    paddingLeft={isRailOpen ? RAIL_GUTTER_PX : 0}
                    marginRight={isRailOpen ? RAIL_GUTTER_PX : 0}
                    overflow="hidden"
                    visibility={isRailOpen ? 'visible' : 'hidden'}
                    transition="width 200ms ease, padding-left 200ms ease, margin-right 200ms ease, visibility 200ms"
                    aria-hidden={!isRailOpen}
                  >
                    <FiltersSidebar facets={facetSidebarFacets} />
                  </Box>
                  <Box
                    flex={1}
                    minWidth={0}
                  >
                    {grid}
                  </Box>
                </Flex>
              );
            })()}
          </PageSection>
        </>
      )}
    </GalleryPhotoPreview>
  );
};

export const DesignsGallery = ({ eventId, eventHandle, onCustomizeCardClickOverride }: DesignsGalleryProps) => {
  const { currentCategory } = useCardCategoryContext();
  const templates = useDesignTemplates({ eventId });
  // Computed once here and threaded down, so the color facet's counts and the
  // filtering they describe are derived from the same colorway matches.
  const colorHits = useColorHits(templates);
  const facetIndex = useTemplateFacetIndex(templates, colorHits);

  const { isPublicShopRoute } = useCardsRouterContext();
  const { products, isLoading: isLoadingProducts } = useProductsWithStatus();
  useFreeShippingBanner(products, !isLoadingProducts);
  const faqItems = useStationeryFaqItems(currentCategory);

  return (
    <>
      <GallerySeo
        currentCategory={currentCategory}
        faqItems={faqItems}
        isPublicShopRoute={isPublicShopRoute}
      />
      <TelemetryProvider>
        <DesignGalleryFiltersProvider>
          {/* Static content — render it without waiting for templates. */}
          <StyleShowcase category={currentCategory} />
          <SkeletonGroup
            isReady={!!templates.length}
            placeholder={<DesignsGallerySkeleton />}
          >
            <ItemImpressionsTelemetryProvider>
              <Root
                templates={templates}
                colorHits={colorHits}
                facetIndex={facetIndex}
                eventId={eventId}
                eventHandle={eventHandle}
                onCustomizeCardClickOverride={onCustomizeCardClickOverride}
              />
              <BackToTop />
            </ItemImpressionsTelemetryProvider>
          </SkeletonGroup>
        </DesignGalleryFiltersProvider>
      </TelemetryProvider>
      {isPublicShopRoute && <RelatedLinks category={currentCategory} />}
      <FaqSection
        faqItems={faqItems}
        category={currentCategory}
      />
      <SeoReviewsSection category={currentCategory} />
      <SeoContentSection category={currentCategory} />
      {isPublicShopRoute && currentCategory === StationeryTemplateCategoryEnum.saveTheDate && <SaveTheDateMarcomFooter />}
    </>
  );
};
